import React from 'react';
import { Text } from '@chatsdet/ui';
import type { DigestHeadline } from '@chatsdet/types';
import { formatDigestDelta, formatDigestRate, formatSuiteFlows } from '@chatsdet/types';

export interface DigestKpiStripProps {
  headline: DigestHeadline;
  size?: 'sm' | 'md';
}

interface Stat {
  key: string;
  label: string;
  value: string;
  valueClassName?: string;
  detail?: string;
  detailClassName?: string;
}

const detailColor = {
  success: 'text-success',
  destructive: 'text-destructive',
  muted: 'text-muted-foreground',
} as const;

/**
 * Compact stat row for the home dashboard's digest card, derived entirely
 * from {@link DigestHeadline} (the digest page has its own period-aware
 * `DigestSinceStats`). One bordered container with a horizontal row of stats separated
 * by dividers, wrapping to two rows on narrow screens, rather than a wall of
 * individual KPI cards.
 */
export function DigestKpiStrip({ headline, size = 'md' }: DigestKpiStripProps) {
  const coverageDeltaText = formatDigestDelta(headline.coverageDelta);
  const coverageDeltaColor =
    headline.coverageDelta === null || headline.coverageDelta === 0
      ? detailColor.muted
      : headline.coverageDelta < 0
        ? detailColor.destructive
        : detailColor.success;

  const coverageDetail =
    headline.coverage === null
      ? 'no snapshot yet'
      : `${coverageDeltaText ? `${coverageDeltaText} over 7 days` : 'over 7 days: —'}${
          headline.capabilitiesAdded > 0 ? ` · ${headline.capabilitiesAdded} added` : ''
        }`;

  const stats: Stat[] = [
    {
      key: 'flows-promoted',
      label: 'Workflows promoted',
      value: String(headline.flowsPromoted),
    },
    {
      key: 'suite-runs',
      label: 'Suite runs',
      value: String(headline.suites.completed),
      detail: formatSuiteFlows(headline.suites),
    },
    {
      key: 'new-issues',
      label: 'New Issues',
      value: String(headline.issuesFiled),
    },
    {
      key: 'open-issues',
      label: 'Open Issues',
      value: String(headline.openIssues),
    },
    {
      key: 'coverage',
      label: 'Coverage',
      value: formatDigestRate(headline.coverage),
      detail: coverageDetail,
      detailClassName: coverageDeltaText ? coverageDeltaColor : detailColor.muted,
    },
  ];

  const isSmall = size === 'sm';

  // Two columns below `md`, five above it. Borders are placed per cell from
  // its index (Tailwind's `divide-*` utilities border every sibling after the
  // first in DOM order, which leaks stray edges on a wrapping grid).
  const cellBorders = (index: number) =>
    [
      'border-border',
      index % 2 === 0 ? '' : 'border-l',
      index >= 2 ? 'border-t' : '',
      index === 0 ? 'md:border-l-0' : 'md:border-l',
      'md:border-t-0',
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="grid grid-cols-2 md:grid-cols-5">
        {stats.map((stat, index) => (
          <div key={stat.key} className={`${cellBorders(index)} ${isSmall ? 'px-2.5 py-2' : 'px-3.5 py-3'}`}>
            <Text
              size="xs"
              className="text-muted-foreground uppercase tracking-wide truncate block"
              title={stat.label}
            >
              {stat.label}
            </Text>
            <Text
              className={`${isSmall ? 'text-base' : 'text-lg md:text-xl'} font-semibold tabular-nums leading-tight ${stat.valueClassName ?? ''}`}
            >
              {stat.value}
            </Text>
            {stat.detail && (
              <Text size="xs" className={stat.detailClassName}>
                {stat.detail}
              </Text>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
